import Marionette from "../../shared/marionette-mustache";
import { autoinject } from "aurelia-dependency-injection";
import { backboneViewProperties } from "../../decorators/backbone-properties";
import { BrowsePanelModel } from "../models/browse-panel";

const dom = {
   BROWSE_LINKS : ".browse-link",
   BROWSE_TABS : ".browse-tab",
   BROWSE_LOADER : ".browse-loading",
   BROWSE_PANEL : ".browse-panel",
   BROWSE_FILTER_INPUT : ".browse-filter-box-input",
   PANEL_TYPE_OPTION : ".panel-type-option",
   BROWSE_FILTER_COUNTER: "#match-item-counter",
   NO_MATCH_FOUND: ".no-match-found"
};

@autoinject
@backboneViewProperties({
    tagName: "div",
    className: "browse-panel"
})
export class BrowsePanelView extends Marionette.ItemView<BrowsePanelModel> {
    constructor(options) {
        super(options);
        this.setupModelListeners();
    }

    public events(): Backbone.EventsHash {
        const events: Backbone.EventsHash = {};
        events["click " + dom.BROWSE_LINKS] = (e) => this.browseLinkClick(e);
        events["click " + dom.PANEL_TYPE_OPTION] = (e) => this.panelTypeClick(e);
        events["keyup " + dom.BROWSE_FILTER_INPUT] = (e) => this.browseFilterKeyUp(e);
        events["keydown " + dom.BROWSE_FILTER_INPUT] = (e) => this.browseFilterKeyDown(e);
        return events;
    }

    public onShow() {
        setTimeout(() => {
            this.$(dom.BROWSE_PANEL).removeClass("prep");
        }, 20);
        this.model.load().then(() => {
           this.model.setCountVisibility();
           this.render();
           this.setSelectedItem();
           this.setDocumentsButtonVisibility();
       });
    }

    private setDocumentsButtonVisibility() {
        if (this.model.ListItems.length === 0) {
            const data = (this.model.collection as any).parents[0].DocumentCount;
            this.$(".show-docs-button").text(`Show document${data.count > 1 ? "s" : ""}`);
        }
    }

    private setupModelListeners() {
        this.listenTo(this.model, "change:Loading", this.loadingChanged);
        this.listenTo(this.model, "change:Value", this.valueChanged);
        this.listenTo(this.model, "change:SelectedType", this.selectedTypeChanged);
        this.listenTo(this.model, "change:FullTextOnly", this.fullTextOnlyChanged);
        this.listenTo(this.model, "change:SubscriptionOnly", this.subscriptionOnlyChanged);
    }

    private loadingChanged(loading: boolean) {
        this.$(dom.BROWSE_LOADER).toggle(loading);
    }

    private panelTypeClick(e: JQueryMouseEventObject) {
        const target = $(e.currentTarget);
        if (target.hasClass("disabled")) {
            return;
        }
        const type = target.attr("data-panel-type");
        const url = this.buildUrl(type);
        Backbone.history.navigate(url, {trigger: true});
    }

    private buildUrl(type) {
        let url = "browse/";
        this.model.PreRequisites.each((pr) => {
            url = `${url}${pr.SelectedType}_${pr.Value}/`;
        });
        url = `${url}${type}`;
        return url;
    }

    private valueChanged() {
        const target = this.$(`${dom.BROWSE_LINKS}[data-id="${this.model.Value}"]`).addClass("active");
        this.$(dom.BROWSE_LINKS).not(target).removeClass("active");
        target.addClass("active");
    }

    private setSelectedItem() {
        _.defer(() => {
            if (!this.model.Value) {
                return;
            }
            this.$(`${dom.BROWSE_LINKS}[data-id="${this.model.Value}"]`).addClass("active");
        });
    }

    private browseFilterKeyDown(e: JQueryKeyEventObject) {
        this.highlightFilterInputboxContent(e);
        if (e.which === 13) { /* if ENTER key pressed */
            e.preventDefault();
            return false;
        }
    }

    private browseFilterKeyUp(e: JQueryKeyEventObject) {
        this.$(dom.NO_MATCH_FOUND).hide();
        this.$(dom.BROWSE_LINKS).show();

        const filterText = $(e.currentTarget).text().toLowerCase().replace(/\u00a0/g, " ");

        const matches = this.$(dom.BROWSE_LINKS).filter((index, link) => {
            const isMatch = $(link).data("itemname").toLowerCase().indexOf(filterText) !== -1;
            return isMatch;
        });
        this.$(dom.BROWSE_LINKS).not(matches).hide();

        if (filterText === "") {
            this.removeHighlightFilterInputboxContent(e);
            this.showListHeadings();
        }
        else {
            this.highlightFilterInputboxContent(e);
            this.hideListHeadings();
        }

        this.removeHighlight();
        if (matches.length > 0) {
            this.highlightText(matches, filterText);
        }
        else {
            this.$(dom.NO_MATCH_FOUND).show();
        }

        const filterCountMsg = matches.length + " of " +  this.$(dom.BROWSE_LINKS).length;
        this.$(dom.BROWSE_FILTER_COUNTER).text(filterCountMsg);

    }

    private removeHighlight() {
        this.$(dom.BROWSE_LINKS).each((index, item: HTMLElement) => {
            const txt = $(item).data("itemname") as string;
            $(item).find("label").text(txt);
        });
    }

    private fullTextOnlyChanged() {
        this.reloadPanel();
    }

    private selectedTypeChanged() {
      this.reloadPanel();
    }

    private subscriptionOnlyChanged() {
        this.reloadPanel();
    }

    private reloadPanel() {
        this.model.ListItems.reset();
        this.model.Loading = true;
        this.render();
        _.defer(async () => {
             await this.model.load();
             this.render();
             this.setSelectedItem();
         });
    }

    private browseLinkClick(e: JQueryMouseEventObject) {
        e.preventDefault();
        const target = $(e.currentTarget);
        if (target.is("[disabled]")) {
            return;
        }
        const url = target.attr("href");
        this.$(dom.BROWSE_LINKS).not(target).removeClass("active");
        target.addClass("active");
        Backbone.history.navigate(url, {trigger: true});
    }

    private highlightText(matches: any, filterText: string) {
        if (filterText.length === 0) { return; }

        matches.each((index, item: HTMLElement) => {
            let text = $(item).data("itemname") as string;
            let matchIndex = text.toLowerCase().indexOf(filterText);
            let position = 0;
            const before = "<span class='highlight'>";
            const after = "</span>";

            while (matchIndex >= 0) {
                const itemEnd = text.substr(matchIndex + filterText.length);
                const itemStart = text.substr(0, matchIndex);
                text = itemStart + before + text.substr(matchIndex, filterText.length) + after + itemEnd;

                position = before.length + after.length + matchIndex + 1;
                matchIndex = text.toLowerCase().indexOf(filterText, position);
            }

            const newValue = text;
            $(item).find("label").html(newValue);
        });
    }

    private highlightFilterInputboxContent(e: JQueryKeyEventObject) {
        const target = $(e.currentTarget);
        target.addClass("active");
    }

    private removeHighlightFilterInputboxContent(e: JQueryKeyEventObject) {
        const target = $(e.currentTarget);
        target.removeClass("active");
    }

    private showListHeadings() {
        this.$("li.removedpriority").addClass("priority").removeClass("removedpriority");
    }

    private hideListHeadings() {
        this.$("li.priority").addClass("removedpriority").removeClass("priority");
    }
}



// WEBPACK FOOTER //
// ./~/tslint-loader??ref--0!./src/browse/views/browse-panel.ts